import { CALL_ADDITIONAL_DOCUMENT_TYPES } from "@features/vessel-call/create/constants";
import type { RequestDeparture } from "@services/departure/types";
import { z } from "zod";
import { getRequestDepartureFormSchema } from "../schema/requestDepartureFormSchema";

type RequestDepartureFormData = z.infer<ReturnType<typeof getRequestDepartureFormSchema>>;

const mapAdditionalDocumentSpecify = (type: unknown, value?: string | null): string | null => {
  if (type !== CALL_ADDITIONAL_DOCUMENT_TYPES.OTHER_SPECIFY) return null;

  const specify = typeof value === "string" ? value.trim() : "";
  return specify || null;
};

const toDateOnly = (value?: string | Date | null): string | undefined => {
  if (!value) return undefined;

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) return undefined;

    const isoDateMatch = trimmedValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDateMatch) {
      return isoDateMatch[1];
    }

    const parsedDate = new Date(trimmedValue);
    if (Number.isNaN(parsedDate.getTime())) return undefined;

    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    return `${parsedDate.getFullYear()}-${month}-${day}`;
  }

  if (Number.isNaN(value.getTime())) return undefined;

  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${value.getFullYear()}-${month}-${day}`;
};

const mapRequestDepartureFormToPayload = (
  vesselCallId: string,
  formData: RequestDepartureFormData,
  initialCustomsClearance?: { name: string; url: string },
): RequestDeparture => {
  return {
    vesselCallId: vesselCallId,
    isFirstVisit: formData.firstVisit,
    masterName: formData.masterName,
    nextPort: {
      portCode: formData.nextPort,
    },
    finalDestination: {
      portCode: formData.finalDestination,
    },
    requestAnchorageClearance: formData.requestAnchorageClearance,
    vesselCondition: {
      conditionCode: formData.vesselCondition,
    },
    draftFore: formData.draftFore,
    draftAft: formData.draftAft,
    presailingMasterDeclarationDocumentName: formData.preSailingMasterDeclaration?.[0]?.name,
    presailingMasterDeclarationDocumentUrl: formData.preSailingMasterDeclaration?.[0]?.url,
    cargoManifestDocumentName: formData.cargoManifest?.[0]?.name,
    cargoManifestDocumentUrl: formData.cargoManifest?.[0]?.url,
    clearanceDocumentName: formData.customsClearance?.[0]?.name ?? initialCustomsClearance?.name,
    clearanceDocumentUrl: formData.customsClearance?.[0]?.url ?? initialCustomsClearance?.url,
    remarks: formData.remarks ?? "",
    additionalDocuments: formData.additionalDocuments?.map((document) => ({
      name: document.documentName || document.name,
      originalFileName: document.name,
      url: document.url,
      type: document.documentType || document.type,
      specify: mapAdditionalDocumentSpecify(
        document.documentType || document.type,
        document.otherSpecifyType,
      ),
      issueDate: toDateOnly(document.issueDate),
      expiryDate: toDateOnly(document.expiryDate),
      summary: document.documentSummary,
    })),
  };
};
export default mapRequestDepartureFormToPayload;
